import type { IframeHTMLAttributes, VoidFunctionComponent } from 'react';

import { useOnMount } from '../../../../../../../hooks';

type Props = {
  format: string;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  setErrorMessage: (errorMessage: string) => void;
  previewURL: string;
};

/**
 * Render the report preview content in an iframe.
 */
const HTMLPreviewContent: VoidFunctionComponent<Props> = ({
  format,
  isLoading,
  previewURL,
  setIsLoading,
  setErrorMessage,
}: Props) => {
  useOnMount(() => {
    setErrorMessage('');
    setIsLoading(true);
  });

  const options: { sandbox?: string } = {};
  if (format !== 'PDF') {
    // these options will disable a pdf from showing in an iframe
    options.sandbox = 'allow-same-origin allow-scripts';
  }

  const onFrameLoad: IframeHTMLAttributes<HTMLIFrameElement>['onLoad'] = (evt) => {
    setIsLoading(false);
    if (format === 'PDF') {
      return;
    }
    const frameWindow = evt.currentTarget.contentWindow;
    if (frameWindow && frameWindow.document) {
      // If there is only a "pre" element at the top level and its contents are JSON with a "message" property, then
      // we have an error that we can display neatly.
      const children = frameWindow.document.body.children;
      if (children.length === 1 && children[0]?.nodeName === 'PRE') {
        const contents: string = children[0].innerHTML;
        try {
          const contentsJSON = JSON.parse(contents);
          if (contentsJSON.message) {
            setErrorMessage(contentsJSON.message);
          }
        } catch (e) {
          // The server did not respond with an error object.
        }
      }
    }
  };

  return (
    <iframe
      title="attribution-preview"
      className={format === 'PDF' ? 'attribution-frame-pdf' : 'attribution-frame'}
      onLoad={onFrameLoad}
      style={isLoading ? { display: 'none' } : {}}
      src={previewURL}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...options}
    />
  );
};

export default HTMLPreviewContent;
